import { Box, Grid, Paper, styled, TextareaAutosize, Typography } from '@mui/material';
import { appRoutes } from 'app/app-routes';
import FavoriteIcons from 'common/components/FavoriteIcons';
import ImageCarousel from 'common/components/ImageCarousel';
import { formatters } from 'common/tools/formatters';
import { BodyTypeValues } from 'common/types/body-type';
import { ColorType, ColorTypeValues } from 'common/types/color-type';
import { ConditionType, ConditionTypeValues } from 'common/types/condition-type';
import { CylinderTypeValues } from 'common/types/cylinder-type';
import { DoorTypeValues } from 'common/types/door-type';
import { DrivetrainTypeValues } from 'common/types/drivetrain-type';
import { FuelTypeValues } from 'common/types/fuel-type';
import { ListingStatus, ListingStatusValues } from 'common/types/listing-status';
import { TNull } from 'common/types/t-null';
import { TitleStatus, TitleStatusValues } from 'common/types/title-status';
import { TransmissionTypeValues } from 'common/types/transmission-type';
import ItemContactInfo from 'features/search/ItemContactInfo';
import {
  BodyType,
  CylinderType,
  DoorType,
  DrivetrainType,
  FuelType,
  Listing,
  TransmissionType,
  useListingsContactSellerMutation,
  User,
} from 'graphql/generated';
import moment from 'moment';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import { cacheActions } from 'redux/cache.slice';
import WsButton from 'stories/inputs/WsButton';
import WsFlexbox from 'stories/layout/WsFlexbox';

import ContactSellerDialog from './ContactSellerDialog';
import { buildSpecItem } from './SpecItem';

const CustomizedTextArea = styled(TextareaAutosize)`
  outline: none;
  width: 90%;
  resize: none;
  S &:focus {
    border: none;
  }
  border: none;
  &:focus-visible {
    border: none;
  }
`;

const ListingView = (props: {
  favorite: boolean;
  listing?: Listing;
  user?: User;
  readOnly?: boolean;
  favoriteToggleFn?: (value: boolean) => void;
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [contactInfoVisible, contactInfoVisibleTo] = React.useState(false);
  const [contactSellerDlgOpen, contactSellerDlgOpenTo] = React.useState(false);
  const [contactSellerFn] = useListingsContactSellerMutation();

  const getCustomFeature = (value: string, label: string, other?: TNull<string>) =>
    value === 'other' ? other || '' : label;

  const onCloseContactSellerDlg = async (ack: boolean, message?: string) => {
    // console.log('close contact', message);
    contactSellerDlgOpenTo(false);
    if (ack) {
      try {
        await contactSellerFn({
          variables: { listingId: props.listing!.id, userId: props.user!.id, message: message || '' },
        });
        dispatch(
          cacheActions.addSnack({
            message: 'Seller message sent!',
            variant: 'success',
          }),
        );
      } catch (error: any) {
        dispatch(
          cacheActions.addSnack({
            message: error.message,
            variant: 'error',
          }),
        );
      }
    }
  };

  return (
    <>
      <ContactSellerDialog
        open={contactSellerDlgOpen}
        closeAction={onCloseContactSellerDlg}
        carDescription={`${props.listing?.heading || ''} - ${props.listing?.trim || ''}`}
      />
      <Grid container justifyContent="center">
        <Grid item xs={12}>
          {props.listing && (
            <Paper elevation={2} sx={{ padding: 2 }}>
              <Grid container>
                <Grid item xs={11}>
                  {props.listing.viewCount && (
                    <Typography color="error" variant="h6" mb={0.5} ml={0.5}>
                      {`Viewed ${props.listing.viewCount} time${props.listing.viewCount > 1 ? 's' : ''}`}
                    </Typography>
                  )}
                  <Typography color="primary" variant="h4">
                    {`${props.listing.heading} - ${props.listing.trim}`}
                  </Typography>
                </Grid>
                <Grid item xs={1}>
                  <Box sx={{ paddingRight: 3, paddingTop: 1 }}>
                    <FavoriteIcons
                      value={props.favorite}
                      readOnly={props.readOnly}
                      listingId={props.listing.id}
                      toggleFn={props.favoriteToggleFn}
                      includeLabel={true}
                      user={props.user}
                    />
                  </Box>
                </Grid>
                <Grid item xs={8}>
                  <ImageCarousel images={props.listing.images} />
                </Grid>
                <Grid item xs={4} sx={{ padding: 0, paddingLeft: 3 }}>
                  <Grid item xs={12}>
                    <Typography color="primary" variant="h4" sx={{ marginBottom: 1 }}>
                      {formatters.currency.format(props.listing.price)}
                    </Typography>
                    <Typography variant="h6">{`${props.listing.mileage.toLocaleString('en-US')} miles`}</Typography>
                    <Typography variant="subtitle1">
                      Location: {`${props.listing.city}, ${props.listing.state} ${props.listing.zipCode}`}
                    </Typography>
                    <Grid container sx={{ marginTop: 1 }}>
                      {buildSpecItem('Listing Date', moment(props.listing.listingDate).format('MMM DD, YYYY'))}
                      {buildSpecItem('Listing Status', ListingStatusValues[props.listing.status as ListingStatus])}
                      {buildSpecItem('Title Status', TitleStatusValues[props.listing.titleStatus as TitleStatus])}
                      {buildSpecItem(
                        'Exterior Color',
                        getCustomFeature(
                          props.listing.colorType,
                          ColorTypeValues[props.listing.colorType as ColorType],
                          props.listing.colorTypeOther,
                        ),
                      )}
                      {buildSpecItem('Condition', ConditionTypeValues[props.listing.conditionType as ConditionType])}
                      {buildSpecItem(
                        'Body Style',
                        getCustomFeature(
                          props.listing.bodyType,
                          BodyTypeValues[props.listing.bodyType as BodyType],
                          props.listing.bodyTypeOther,
                        ),
                      )}
                      {buildSpecItem(
                        'Doors',
                        getCustomFeature(
                          props.listing.doorType,
                          DoorTypeValues[props.listing.doorType as DoorType],
                          props.listing.doorTypeOther,
                        ),
                      )}
                      {buildSpecItem(
                        'Cylinders',
                        getCustomFeature(
                          props.listing.cylinderType,
                          CylinderTypeValues[props.listing.cylinderType as CylinderType],
                          props.listing.cylinderTypeOther,
                        ),
                      )}
                      {buildSpecItem(
                        'Drivetrain',
                        getCustomFeature(
                          props.listing.drivetrainType,
                          DrivetrainTypeValues[props.listing.drivetrainType as DrivetrainType],
                          props.listing.drivetrainTypeOther,
                        ),
                      )}
                      {buildSpecItem(
                        'Transmission',
                        getCustomFeature(
                          props.listing.transmissionType,
                          TransmissionTypeValues[props.listing.transmissionType as TransmissionType],
                          props.listing.transmissionTypeOther,
                        ),
                      )}
                      {buildSpecItem(
                        'Fuel',
                        getCustomFeature(
                          props.listing.fuelType,
                          FuelTypeValues[props.listing.fuelType as FuelType],
                          props.listing.fuelTypeOther,
                        ),
                      )}
                    </Grid>
                    <Grid container sx={{ marginTop: 1 }}>
                      <Grid item xs={12}>
                        <Typography color="primary" variant="h6" sx={{ marginTop: 1 }}>
                          Additional Description
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <CustomizedTextArea readOnly value={props.listing.description} />
                      </Grid>
                    </Grid>
                    <Grid container sx={{ marginTop: 2 }}>
                      <Grid item xs={12}>
                        <Typography color="primary" variant="h6" sx={{ marginBottom: 1 }}>
                          Contact Info
                        </Typography>
                      </Grid>
                      {props.user ? (
                        <>
                          {props.listing.showContactInfo ? (
                            <Grid item xs={12}>
                              {contactInfoVisible && !props.readOnly ? (
                                <ItemContactInfo listingId={props.listing!.id} />
                              ) : (
                                <WsButton
                                  variant="outlined"
                                  label="Show Contact Info"
                                  onClick={() => !props.readOnly && contactInfoVisibleTo(true)}
                                />
                              )}
                            </Grid>
                          ) : (
                            <WsButton
                              variant="outlined"
                              label="Contact Seller"
                              onClick={() => !props.readOnly && contactSellerDlgOpenTo(true)}
                            />
                          )}
                        </>
                      ) : (
                        <Grid container justifyContent="center" width="80%">
                          <Typography color="primary" variant="body1" sx={{ marginBottom: 1 }}>
                            To protect the privacy of our users, you must logged in to view contact information.
                          </Typography>
                          <Grid container justifyContent="center">
                            <NavLink to={appRoutes.login}>Log In</NavLink>
                            &nbsp;&nbsp;or&nbsp;&nbsp;
                            <NavLink to={appRoutes.register}>Create an Account</NavLink>
                          </Grid>
                        </Grid>
                      )}

                      {/* {props.listing.showContactInfo ? (
                        <Grid item xs={12}>
                          {contactInfoVisible && !props.readOnly ? (
                            <ItemContactInfo listingId={props.listing!.id} />
                          ) : (
                            <WsButton
                              variant="outlined"
                              label="Show Contact Info"
                              onClick={() => !props.readOnly && contactInfoVisibleTo(true)}
                            />
                          )}
                        </Grid>
                      ) : (
                        <>
                          {props.user ? (
                            <WsButton
                              variant="outlined"
                              label="Contact Seller"
                              onClick={() => !props.readOnly && contactSellerDlgOpenTo(true)}
                            />
                          ) : (
                            <Grid container justifyContent="center" width="80%">
                              <Typography color="primary" variant="body1" sx={{ marginBottom: 1 }}>
                                To protect the privacy of our users, you must logged in to contact a seller.
                              </Typography>
                              <Grid container justifyContent="center">
                                <NavLink to={appRoutes.login}>Log In</NavLink>
                                &nbsp;&nbsp;or&nbsp;&nbsp;
                                <NavLink to={appRoutes.register}>Create an Account</NavLink>
                              </Grid>
                            </Grid>
                          )}
                        </>
                      )} */}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default ListingView;
